'use client';

import cx from 'classix';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import {
  type ChangeEvent,
  useEffect,
  useState,
} from 'react';
import { useUnmount } from 'usehooks-ts';

import {
  type FiltersColumns,
  type ListingsFilterToggle,
} from '@/features/listing/types/listing-filters-types';
import Toggle from '@/ui/form-elements/toggle/toggle';

import styles from './listing-filter-toggle.module.scss';

interface ListingFilterToggleProps<Label> {
  filter: ListingsFilterToggle<Label>;
  setFilterLink: (link: { pathname: string; searchParams: URLSearchParams }) => void;
  filterLink: {
    pathname: string;
    searchParams: URLSearchParams;
  }
  columns?: FiltersColumns;
}

export default function ListingFilterToggle<Label extends string>(props: ListingFilterToggleProps<Label>) {
  const {
    columns = '1',
    filter,
    filterLink,
    setFilterLink,
  } = props;

  const translationsFilters = useTranslations('listing.filters');

  const searchParams = useSearchParams();

  const [selectedValues,
    setSelectedValues] = useState<string[]>([]);

  const creteNewSearchParams = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;

    if (filter.filterType === 'radio') {
      filterLink.searchParams.set(filter.name, event.target.value);

      if (event.target.value === filter.defaultValue) {
        filterLink.searchParams.delete(filter.name, event.target.value);
      }

      setSelectedValues([selectedValue]);
    } else {
      const updatedValues = selectedValues.includes(selectedValue)
        ? selectedValues.filter((value) => value !== selectedValue)
        : [...selectedValues, selectedValue];

      if (updatedValues.length === 0) {
        filterLink.searchParams.delete(filter.name);
      } else {
        filterLink.searchParams.set(filter.name, updatedValues.join(','));
      }

      setSelectedValues(updatedValues);
    }

    setFilterLink({
      pathname: filterLink.pathname,
      searchParams: filterLink.searchParams,
    });
  };

  useEffect(() => {
    const defaultValue = filterLink.searchParams.get(filter.name) ?? filter.defaultValue;

    if (defaultValue) {
      setSelectedValues(defaultValue.split(','));
    } else {
      setSelectedValues([]);
    }
  }, [filter.defaultValue, filter.name, filterLink, searchParams]);

  useUnmount(() => {
    setFilterLink({
      pathname: filterLink.pathname,
      searchParams: new URLSearchParams(searchParams.toString()),
    });
  });

  return (
    <div
      className={cx(
        styles.listingFilterContainer,
        styles[`listingFilterContainer__col${columns}`],
        styles[`listingFilterContainer__${filter.optionsVariant ?? 'standard'}`],
      )}
    >
      {filter.options.map((option) => {
        const Icon = option.icon;

        return (
          <Toggle
            onChange={creteNewSearchParams}
            type={filter.filterType}
            id={option.value}
            name={`${filter.name}[]`}
            value={option.value}
            checked={selectedValues.includes(option.value)}
            variant={filter.optionsVariant}
            key={option.value}
          >
            <div className={styles.listingFilterContainer_filter}>
              {Icon && (
                <Icon size={18} />
              )}
              <span>
                {filter.isOptionsTranslated && filter.optionsTranslatedKey
                  // @ts-expect-error - need to add all possible values
                  ? translationsFilters(`${filter.optionsTranslatedKey}.${option.label}`)
                  : option.label}
              </span>
            </div>
          </Toggle>
        );
      })}
    </div>
  );
}
